import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DrawerEntitiesNames,Operations } from 'common/types/TypeDefs';
import { ConflictErrorOrigin } from 'common/utils/LicenseAclEntriesErrorHelpers';
import { hasText } from 'common/utils/trimHelpers';

interface EditDrawerState {
    open: boolean,
    saveButtonEnabled: boolean,
    currentOperation?: Operations,
    currentEntity?: DrawerEntitiesNames,
    nameInConflict?: boolean,
    conflictErrorOrigin?: ConflictErrorOrigin,
    currentTenantId?: string,
    currentDrawerId?: string;
}

type StateWithEditDrawer = { editDrawer: EditDrawerState };

const initialState: EditDrawerState = {
    open: false,
    saveButtonEnabled: false
};

const editDrawerSlice = createSlice({
    name: 'editDrawer',
    initialState,

    reducers: (create) => ({
        openDrawer: create.reducer((state: EditDrawerState) => {
            state.open = true;
        }),

        closeDrawer: create.reducer((state: EditDrawerState) => {
            if (state.nameInConflict !== undefined) {
                state.nameInConflict = false;
            }
            if (hasText(state.conflictErrorOrigin)) {
                state.conflictErrorOrigin = undefined;
            }
            state.open = false;
            state.saveButtonEnabled = false;
        }),

        setOpenDrawer: create.reducer((state, action: PayloadAction<boolean>) => {
            state.open = action.payload;
        }),

        setSaveButtonEnabled: create.reducer((state, action: PayloadAction<boolean>) => {
            state.saveButtonEnabled = action.payload;
        }),

        setNameInConflict: create.reducer((state, action: PayloadAction<boolean | undefined>) => {
            state.nameInConflict = action.payload;
        }),

        setConflictErrorOrigin: create.reducer((state, action: PayloadAction<ConflictErrorOrigin | undefined>) => {
            state.conflictErrorOrigin = action.payload;
        }),

        setCurrentOperation: create.reducer((state, action: PayloadAction<Operations | undefined>) => {
            state.currentOperation = action.payload;
        }),

        setCurrentEntity: create.reducer((state, action: PayloadAction<DrawerEntitiesNames | undefined>) => {
            state.currentEntity = action.payload;
        }),

        setCurrentTenantId: create.reducer((state, action: PayloadAction<string>) => {
            state.currentTenantId = action.payload;
        }),

        setCurrentDrawerId: create.reducer((state, action: PayloadAction<string>) => {
            state.currentDrawerId = action.payload;
        })
    })
});

// eslint-disable-next-line no-restricted-syntax
export default editDrawerSlice.reducer;
export const { openDrawer, closeDrawer, setSaveButtonEnabled, setNameInConflict, setConflictErrorOrigin, setCurrentOperation, setCurrentEntity, setCurrentTenantId, setCurrentDrawerId, setOpenDrawer } = editDrawerSlice.actions;
export const selectSaveButtonEnabled = (state: StateWithEditDrawer) => state.editDrawer.saveButtonEnabled;
export const selectNameInConflict = (state: StateWithEditDrawer) => state.editDrawer.nameInConflict;
export const selectConflictErrorOrigin = (state: StateWithEditDrawer) => state.editDrawer.conflictErrorOrigin;
export const selectOpen = (state: StateWithEditDrawer) => state.editDrawer.open;
export const selectCurrentOperation = (state: StateWithEditDrawer) => state.editDrawer.currentOperation;
export const selectCurrentEntity = (state: StateWithEditDrawer) => state.editDrawer.currentEntity;
export const selectCurrentTenantId = (state: StateWithEditDrawer) => state.editDrawer.currentTenantId;
export const selectCurrentDrawerId = (state: StateWithEditDrawer) => state.editDrawer.currentDrawerId;
